import {
    requestInventory,
    requestVehiclesReadiness,
    sendErrorNotification,
} from 'helpers/web_to_client'
import { isVehicleReadyForBattle } from 'helpers/vehicles'
import { httpRequest } from 'http'

import errors from 'errors'
import { formatMessage } from 'locale'
import {
    clearSelectedVehicleCds,
    restoreVehicleCds,
    restoreAllVehicleCds,
    restoreVehicleCdsByLevel,
    restoreVehicleFilter,
    saveAllVehicleCds,
    saveFilter,
    saveSelectedVehicleCds,
} from 'helpers/playersMatching'

export const BATTLE_MODE_FILTER_SET = 'inventory/BATTLE_MODE_FILTER_SET'
export const DISABLE_MATCHING = 'player_matching/DISABLE'
export const ENABLE_MATCHING = 'player_matching/ENABLE'
export const FULL_UPDATE_FROM_SERVER = 'players_matching/FULL_UPDATE_FROM_SERVER'
export const INVENTORY_READINESS_UPDATE = 'inventory/READINESS_UPDATE'
export const REQUEST_ERROR = 'players_matching/REQUEST_ERROR'
export const REQUEST_START = 'inventory/REQUEST_START'
export const REQUEST_SUCCESS = 'inventory/REQUEST_SUCCESS'
export const SMALL_UPDATE_FROM_SERVER = 'players_matching/SMALL_UPDATE_FROM_SERVER'
export const START_MATCHING = 'player_matching/START'
export const STOP_MATCHING = 'player_matching/STOP'
export const VEHICLES_FILTER_STATE_CHANGED = 'inventory/VEHICLES_FILTER_STATE_CHANGED'
export const VEHICLES_SELECT = 'inventory/VEHICLES_SELECT'

export function startRequest() {
    return {
        type: REQUEST_START,
        payload: {},
    }
}

export function requestSuccess(response) {
    return {
        type: REQUEST_SUCCESS,
        payload: {
            inventory: response,
        },
    }
}

export function requestError(errorData) {
    return dispatch => {
        sendErrorNotification(errorData)
        dispatch({
            type: REQUEST_ERROR,
            payload: errorData,
        })
        dispatch({
            type: STOP_MATCHING,
        })
    }
}

export function requestVehicles() {
    return async (dispatch, getState) => {
        const playersMatchingState = getState().players_matching
        if (playersMatchingState.loading) return

        try {
            const respData = await requestInventory()
            const templates = getState().settings.iconUrlTemplates.vehicles
            const { isInGameBrowser } = getState().settings

            const inventory = respData.map(vehicle => ({
                ...vehicle,
                iconUrl: getVehicleImageUrl(
                    vehicle,
                    (isInGameBrowser ? templates.large.client : templates.large.glossary)
                ),
                smallIconUrl: getVehicleImageUrl(
                    vehicle,
                    (isInGameBrowser ? templates.small.client : templates.small.glossary)
                ),
            }))

            dispatch(updateSelectedVehicles(respData.map(vehicle => vehicle.id)))

            dispatch(requestSuccess(inventory))

        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('>> WGSH cannot request vehicles', e)
            dispatch(requestError(e))
        }
    }
}

export function selectDefaultVehicles(clearSavedState = false) {
    return (dispatch, getState) => {
        const { vehicle_level } = getState().players_matching
        if (clearSavedState) clearSelectedVehicleCds(vehicle_level)

        dispatch(selectVehicleCdsByLevel(vehicle_level))
    }
}

/**
 * Vehicles list was updated. Need to remove unchecked items from selectedVehiclesCds and add new
 * @param nextVehicleCds List of all vehicles in inventory update
 */
export const updateSelectedVehicles = nextVehicleCds => {
    return (dispatch, getState) => {
        const playersMatchingState = getState().players_matching
        const lastStoredVehicleCds = restoreAllVehicleCds()

        const prevVehicles = playersMatchingState.vehicles
        const prevVehicleCds = prevVehicles.length ?
            prevVehicles.map(vehicle => vehicle.id) :
            lastStoredVehicleCds

        const newVehicles = lastStoredVehicleCds === null ?
            nextVehicleCds :
            nextVehicleCds.filter(vehicleCd => !lastStoredVehicleCds.includes(vehicleCd))
        saveAllVehicleCds(nextVehicleCds)

        const selectedVehicleCds = playersMatchingState.selected_vehicle_cds
        const hasChangesInSelected = (
            selectedVehicleCds &&
            !prevVehicles.length ||
            JSON.stringify(prevVehicleCds) === JSON.stringify(nextVehicleCds)
        )

        if (hasChangesInSelected && !newVehicles.length) return

        const vehicleLevel = playersMatchingState.vehicle_level

        const uncheckedVehicleCds = prevVehicleCds
            .filter(vehicleCd => !selectedVehicleCds.includes(vehicleCd))

        let nextSelectedVehicleCds = nextVehicleCds
            .filter(vehicleCd => !uncheckedVehicleCds.includes(vehicleCd))

        nextSelectedVehicleCds = Array.from(new Set(nextSelectedVehicleCds.concat(newVehicles)))

        saveSelectedVehicleCds(vehicleLevel, nextSelectedVehicleCds)

        dispatch({
            type: VEHICLES_SELECT,
            payload: { vehicle_cds: nextSelectedVehicleCds },
        })
    }
}

export function setSelectedVehicles(vehicle_cds, filter) {
    return (dispatch, getState) => {
        const { vehicle_level } = getState().players_matching

        if (vehicle_cds) saveSelectedVehicleCds(vehicle_level, vehicle_cds)
        if (filter) saveFilter(vehicle_level, filter)

        dispatch({
            type: VEHICLES_SELECT,
            payload: {
                vehicle_cds,
                filter,
            },
        })
    }
}

export function updateVehiclesReadiness() {
    return async (dispatch, getState) => {
        const inventory = await requestVehiclesReadiness()

        if (inventory.length !== getState().players_matching.vehicles.length) {
            dispatch(requestVehicles())
            return
        }

        dispatch({
            type: INVENTORY_READINESS_UPDATE,
            payload: inventory,
        })
    }
}

export function deselectVehicle(vehicle_cd) {
    return (dispatch, getState) => {
        let selectedVehicleCds = getState().players_matching.selected_vehicle_cds

        if (!selectedVehicleCds.includes(vehicle_cd)) return

        selectedVehicleCds = selectedVehicleCds.filter(id => id !== vehicle_cd)
        dispatch(setSelectedVehicles(selectedVehicleCds))
    }
}

function selectVehicleCdsByLevel(vehicle_level) {
    return (dispatch, getState) => {
        const { vehicles } = getState().players_matching

        let stored_vehicle_cds = restoreVehicleCds(vehicle_level)

        let vehicle_cds
        if (!stored_vehicle_cds) {
            vehicle_cds = restoreVehicleCdsByLevel(vehicles, vehicle_level)
        } else {
            vehicle_cds = stored_vehicle_cds.filter(vehicle_cd => {
                const vehicle = vehicles.find(vehicle => vehicle.id === vehicle_cd)

                if (!vehicle) return false
                if (vehicle.level !== vehicle_level) return false

                return true
            })
        }

        dispatch(setSelectedVehicles(vehicle_cds))
    }
}

export function setBattleModeFilter(vehicle_level) {
    return dispatch => {
        dispatch({
            type: BATTLE_MODE_FILTER_SET,
            payload: {
                vehicle_level,
            },
        })

        dispatch(selectVehicleCdsByLevel(vehicle_level))
    }
}

export function startPlayerMatching() {
    return async (dispatch, getState) => {
        dispatch(startRequest())

        const inventory = await requestInventory()
        const playersMatching = getState().players_matching
        const { selected_vehicle_cds } = playersMatching

        if (selected_vehicle_cds.length === 0) {
            const errorMessage = formatMessage(errors.noSelectedVehicle)
            return dispatch(requestError({ errorMessage }))
        }
        const readyVehicleCds = selected_vehicle_cds.filter(vehicle_cd => {
            const vehicle = inventory.find(vehicle => vehicle.id === vehicle_cd)

            return vehicle && isVehicleReadyForBattle(vehicle)
        })

        if (!readyVehicleCds) {
            const errorMessage = formatMessage(errors.selectedVehiclesNotReady)
            return dispatch(requestError({ errorMessage }))
        }

        try {
            const response = await httpRequest(
                '/game_api/players_matching', 'POST', { vehicle_cds: readyVehicleCds }
            )

            dispatch({
                type: START_MATCHING,
                payload: response,
            })
        } catch (e) {
            dispatch(requestError(e))
        }
    }
}

export const vehiclesFilterOpen = () => ({
    type: VEHICLES_FILTER_STATE_CHANGED,
    payload: { isOpened: true },
})

export const vehiclesFilterClose = () => ({
    type: VEHICLES_FILTER_STATE_CHANGED,
    payload: { isOpened: false },
})

export function stopPlayerMatching() {
    return async (dispatch) => {
        try {
            dispatch({ type: REQUEST_START })
            await httpRequest('/game_api/players_matching', 'DELETE')
            dispatch({ type: STOP_MATCHING })
        } catch (error) {
            dispatch(requestError(error))
        }
    }
}

export function getPlayerMatchingStatus() {
    return async (dispatch, getState) => {
        try {
            const response = await httpRequest('/game_api/players_matching', 'GET')
            const { players_matching } = getState()

            if (
                players_matching.start_time === response.start_time &&
                players_matching.end_time === response.end_time
            ) return

            dispatch({
                type: START_MATCHING,
                payload: response,
            })
        } catch (error) {
            dispatch(requestError(error))
        }
    }
}

export function enablePlayerMatching() {
    return {
        type: ENABLE_MATCHING,
    }
}

export function disablePlayerMatching() {
    return {
        type: DISABLE_MATCHING,
    }
}

function getVehicleImageUrl(vehicle, template) {
    return template.replace(
        '#nation#', vehicle.nation
    ).replace(
        '#techName#', vehicle.techName
    )
}

export function updatePlayersMatching(playersMatchingResponse) {
    return (dispatch, getState) => {
        const playersMatchingState = getState().players_matching

        const playersMatchingUpdate = {
            sortie_settings: playersMatchingResponse.sortie_settings,
            start_time: playersMatchingResponse.start_time,
            end_time: playersMatchingResponse.end_time,
        }

        const enabledLevels = playersMatchingResponse.sortie_settings
            .filter(setting => setting.enabled || setting.show_challenges_message)
            .map(setting => setting.level)

        const vehicleLevel = (
            playersMatchingResponse.vehicle_level ||
            enabledLevels.includes(playersMatchingState.vehicle_level) ?
                playersMatchingState.vehicle_level :
                Math.min(...enabledLevels)
        )

        playersMatchingUpdate.vehiclesFilter = restoreVehicleFilter(vehicleLevel)

        if (!playersMatchingResponse.vehicle_cds.length && vehicleLevel === playersMatchingState.vehicle_level) {
            dispatch({
                type: SMALL_UPDATE_FROM_SERVER,
                payload: { ...playersMatchingUpdate },
            })
            return
        }

        if (playersMatchingResponse.vehicle_cds.length) {
            // Players matching is going -- use its level and selected vehicles
            playersMatchingUpdate.selected_vehicle_cds = playersMatchingResponse.vehicle_cds
            playersMatchingUpdate.vehicle_level = playersMatchingResponse.vehicle_level
        } else if (vehicleLevel !== playersMatchingState.vehicle_level) {
            // We have clear filters and selected vehicles or vehicle level was changed
            playersMatchingUpdate.vehicle_level = vehicleLevel
            playersMatchingUpdate.selected_vehicle_cds = restoreVehicleCds(vehicleLevel) ||
                restoreVehicleCdsByLevel(playersMatchingState.vehicles, vehicleLevel)
        }

        dispatch({
            type: FULL_UPDATE_FROM_SERVER,
            payload: { ...playersMatchingUpdate },
        })
    }
}



// WEBPACK FOOTER //
// ./state/actions/players_matching.js